import { useQuery } from '@apollo/client'
import {
  Button,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  Typography,
  useMediaQuery,
  useTheme
} from '@material-ui/core'
import TimelineIcon from '@material-ui/icons/Timeline'
import { Alert, Skeleton } from '@material-ui/lab'
import Pagination from '@material-ui/lab/Pagination'
import { useMemo, useState } from 'react'

import PageHeader from '../../components/PageHeader'
import PromotionTabs from '../../components/PromotionTabs'
import {
  PROMOTION_SKIP_REASONS_QUERY,
  PromotionSkipReasonsData
} from '../../graphql/promotions'
import { formatCount } from '../../helpers/promotionAmount'
import {
  exceedsRetention,
  SKIP_PERIOD_PRESETS,
  windowFor
} from '../../helpers/promotionPeriod'
import { getTranslate, lang } from '../../localization'
import { LocalizedLink } from '../../routes/localizedRoutes'
import SkipCharts from './SkipCharts'
import SkipPromotionTable from './SkipPromotionTable'
import SkipReasonSummary from './SkipReasonSummary'
import useStyles from './styles/index.styles'

const PER_PAGE = 20

const formatWindowDate = (iso: string | null): string =>
  iso ? new Date(iso).toLocaleDateString(lang === 'fa' ? 'fa-IR' : 'en-GB') : ''

/**
 * Why promotions did NOT apply — the other half of the Overview's question.
 *
 * The Overview says a campaign applied zero times. This says whether nobody qualified, the
 * budget ran out, or it never left draft. A peer view of the same subject, which is what makes
 * it a tab.
 *
 * BOUNDED BY RETENTION, AND THE SCREEN SAYS SO EVERYWHERE. The Overview reads counters that
 * live as long as the promotion, so "all time" is true there. This reads an evaluation trace
 * swept after a fixed horizon, so the widest honest answer is "recently". An operator who
 * reads a zero here as "never happened" rather than "not in the last N days" has been misled
 * by the screen, not by the data — which is why the horizon is stated unconditionally rather
 * than only when a window is applied.
 */
const PromotionSkipReasons = () => {
  const classes = useStyles()
  const theme = useTheme()
  const isSmallScreen = useMediaQuery(theme.breakpoints.down('sm'))

  const [periodKey, setPeriodKey] = useState('all')
  const [page, setPage] = useState(1)

  const period = useMemo(
    () =>
      SKIP_PERIOD_PRESETS.find((p) => p.key === periodKey) ??
      SKIP_PERIOD_PRESETS[0],
    [periodKey]
  )
  const window = useMemo(() => windowFor(period.days), [period])

  const { data, loading, error, refetch } = useQuery<PromotionSkipReasonsData>(
    PROMOTION_SKIP_REASONS_QUERY,
    {
      variables: {
        from: window.from,
        to: window.to,
        page,
        perPage: PER_PAGE
      },
      fetchPolicy: 'cache-and-network'
    }
  )

  const skips = data?.promotionSkipReasons
  const pageCount = skips
    ? Math.ceil(skips.total / (skips.perPage || PER_PAGE))
    : 0
  const clamped = exceedsRetention(period.days, skips?.retentionDays)

  return (
    <div>
      <PageHeader title={getTranslate('Why promotions did not apply')} />
      {/*
        THE h1 IS VISUALLY HIDDEN, and that is the portal's decision rather than a compromise.

        Notifications/index.tsx records trying a visible page title above a tab strip and
        reverting it: "An <h1> here is what made this page read as a different product."
        SupportTabs and InventoryTabs render none either. A visible title here would diverge
        from three screens that already settled this.

        Hiding it costs nothing that matters: a heading-navigation user gets an h1 naming the
        screen — which the tab strip cannot give them, since a selected tab is not a heading —
        and sighted users get the layout every other tabbed surface has. Clipped, not
        display:none, so it stays in the accessibility tree.

        An earlier attempt rendered it visibly at the theme's h3 scale to avoid looking
        anomalous. That premise was wrong twice over: this theme's h1 is 35px rather than
        MUI's default display scale, and h3 carries text.secondary — so the page title came
        out in the palette's de-emphasis grey, a step smaller than the portal's own
        SectionTitle.
      */}
      <Typography variant="srOnly" component="h1">
        {getTranslate('Why promotions did not apply')}
      </Typography>
      <PromotionTabs />

      <div className={classes.controls} data-cy="SkipReasonsControls">
        <Button
          component={LocalizedLink}
          to="/promotions/evaluation-trace"
          role="link"
          variant="text"
          color="primary"
          data-cy="PromotionEvaluationTraceLink"
          startIcon={<TimelineIcon />}>
          {getTranslate('Evaluation trace')}
        </Button>
        <FormControl
          variant="outlined"
          size="small"
          className={classes.periodSelect}>
          <InputLabel id="promotion-skips-period">
            {getTranslate('Period')}
          </InputLabel>
          <Select
            labelId="promotion-skips-period"
            id="promotion-skips-period-select"
            value={periodKey}
            label={getTranslate('Period')}
            data-cy="SkipPeriodSelect"
            onChange={(event) => {
              setPeriodKey(String(event.target.value))
              setPage(1)
            }}>
            {SKIP_PERIOD_PRESETS.map((preset) => (
              <MenuItem key={preset.key} value={preset.key}>
                {getTranslate(preset.label)}
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        {skips && (
          <Typography
            variant="body2"
            className={classes.windowNote}
            data-cy="SkipWindow">
            {skips.periodFrom
              ? getTranslate('Counted {from} – {to}', {
                  from: formatWindowDate(skips.periodFrom),
                  to: formatWindowDate(skips.periodTo)
                })
              : getTranslate('Counted over everything still retained')}
          </Typography>
        )}
      </div>

      {/*
        EVERY NUMBER THROUGH ONE LOCALE-AWARE FORMATTER, including these.

        The dates in the window note above come from toLocaleDateString('fa-IR') and render
        Persian digits; these came from String() and rendered Latin ones. A Persian operator
        read ۱۴۰۵/۰۶/۱۵ in one line and 90 in the next — two numeral systems in adjacent
        sentences. Native digits also carry their own directionality, which removes most of
        the bidi reordering risk around numbers embedded in RTL text.

        THE HORIZON, STATED UNCONDITIONALLY. Read from the response rather than hardcoded: it
        lives in the service's config and has already changed once, and the contract carries
        it on every response precisely so a client can state the bound instead of assuming one.
      */}
      {skips && (
        <Typography
          variant="body2"
          className={classes.retentionNote}
          data-cy="SkipRetentionNote">
          {getTranslate(
            'Evaluations are kept for {days} days — figures here cover that period only, not the whole life of a promotion.',
            { days: formatCount(skips.retentionDays) }
          )}
        </Typography>
      )}

      {/*
        A preset asking for more than is kept is CALLED OUT rather than quietly served. The
        server clamps and returns what it has; without this the label goes on promising a
        year, and an operator reads "no skips in 12 months" when the fact is "none in the days
        we keep". The presets are chosen not to exceed the current horizon, so this fires only
        if retention is lowered — which is exactly when nobody would think to re-check.
      */}
      {clamped && (
        <Alert
          severity="warning"
          className={classes.error}
          data-cy="SkipClampNote">
          {/*
            An Alert, not a coloured paragraph. The previous version differed from the
            ambient retention note by HUE ALONE — same variant, same size, no icon — so the
            one message saying "your period is longer than we keep" was invisible to anyone
            not distinguishing warning.dark from text.secondary.
          */}
          {getTranslate(
            'This period is longer than evaluations are kept, so the figures cover the retained period only.'
          )}
        </Alert>
      )}

      {/*
        THE HORIZON IS IN THE ANNOUNCEMENT, not only in the notes above.

        The window and retention notes are ordinary Typography outside any live region, so a
        screen-reader user changing the period previously heard only counts — a bare zero with
        no period and no horizon, on the one screen whose central risk is reading a zero as
        "never". The announcement carried strictly less than the layout did, which is the
        inverse of the asymmetry accessibility work usually guards against.
      */}
      <div role="status" className={classes.announcement}>
        {loading
          ? getTranslate('Loading skip reasons…')
          : skips
          ? getTranslate(
              '{reasons} reasons across {examined} evaluations in the last {days} days, showing {shown} of {total} promotions',
              {
                reasons: formatCount(skips.byReason.length),
                examined: formatCount(skips.evaluationsExamined),
                days: formatCount(skips.retentionDays),
                shown: formatCount(skips.byPromotion.length),
                total: formatCount(skips.total)
              }
            )
          : ''}
      </div>

      {/*
        THE ERROR TEXT DEPENDS ON WHETHER STALE FIGURES ARE STILL ON SCREEN.

        fetchPolicy is cache-and-network, so a failed refetch leaves the previous response
        rendered underneath this alert — and the period control has already moved. Change the
        period, let the refetch fail, and the tables show the OLD period's figures under the
        NEW period's label, with an alert that says only "could not be loaded". The operator
        cannot tell which window they are looking at, and the numbers look authoritative.

        Saying which case it is costs one conditional. Leaving a window label pointing at data
        from a different window does not announce itself.
      */}
      {error && (
        <Alert
          severity="error"
          className={classes.error}
          data-cy="SkipReasonsError"
          action={
            <Button color="inherit" size="small" onClick={() => refetch()}>
              {getTranslate('Retry')}
            </Button>
          }>
          {skips
            ? getTranslate(
                'The refresh failed. The figures below are the last ones loaded and may not match the period shown.'
              )
            : getTranslate('The skip reasons could not be loaded.')}
        </Alert>
      )}

      <div aria-busy={loading}>
        {loading && !skips ? (
          <>
            <Skeleton variant="rect" height={160} data-cy="SkipSkeleton" />
            <Skeleton variant="rect" height={240} style={{ marginTop: 16 }} />
          </>
        ) : (
          skips && (
            <>
              {/* Charts ABOVE the tables, which remain the complete view: the charts cap at
                  eight bars and the by-promotion one sums across reasons, so the tables carry
                  strictly more. They are aria-hidden for that reason. */}
              <SkipCharts
                byReason={skips.byReason}
                byPromotion={skips.byPromotion}
              />
              <SkipReasonSummary
                byReason={skips.byReason}
                evaluationsExamined={skips.evaluationsExamined}
                retentionDays={skips.retentionDays}
                onRefresh={refetch}
                isSmallScreen={isSmallScreen}
              />
              <SkipPromotionTable
                rows={skips.byPromotion}
                onRefresh={refetch}
                isSmallScreen={isSmallScreen}
              />
              {pageCount > 1 && (
                <div className={classes.paginationContainer}>
                  <Pagination
                    count={pageCount}
                    color="primary"
                    size={isSmallScreen ? 'small' : 'medium'}
                    page={page}
                    onChange={(_, value) => setPage(value)}
                  />
                </div>
              )}
            </>
          )
        )}
      </div>
    </div>
  )
}

export default PromotionSkipReasons
